/** Raid sim event kinds — Phaser-free. */

export const RaidEvent = {
  Hit: "hit",
  Heal: "heal",
  Telegraph: "telegraph",
  BrassResolve: "brassResolve",
  LeapTelegraph: "leapTelegraph",
  LeapLand: "leapLand",
  /** Boss basic-attack impact — melee reach circle (one per swing resolve). */
  MeleeImpact: "meleeImpact",
  Break: "break",
  Phase: "phase",
  Encounter: "encounter",
  Order: "order",
  /** Per-unit speech bark near a hero (commander gesture ack). */
  Bark: "bark",
  Spawn: "spawn",
  Death: "death",
  Win: "win",
  Lose: "lose",
} as const;

export type RaidEventKind = (typeof RaidEvent)[keyof typeof RaidEvent];

export type RaidSimEvent =
  | {
      kind: typeof RaidEvent.Hit;
      x0: number;
      y0: number;
      x1: number;
      y1: number;
      damage: number;
      attackerId?: number;
      targetKind: "boss" | "unit";
      targetId?: number;
      /** Party class active id when this Hit is from an offensive skill (e.g. marked_shot). */
      skillId?: string;
    }
  | {
      kind: typeof RaidEvent.Heal;
      x: number;
      y: number;
      amount: number;
      targetId: number;
      healerId: number;
    }
  | {
      kind: typeof RaidEvent.Telegraph;
      shape: "circle" | "cone" | "line";
      x: number;
      y: number;
      range: number;
      coneDeg?: number;
      aimAngle?: number;
      remain: number;
      duration: number;
      danger: "yellow" | "red";
      skillId: "slam" | "brass" | "leap" | "charge";
      halfWidth?: number;
      originPad?: number;
    }
  | {
      kind: typeof RaidEvent.BrassResolve;
      x: number;
      y: number;
      hits: number;
      danger: "yellow" | "red";
      skillId: "slam" | "brass" | "leap" | "charge";
      range: number;
      coneDeg: number;
      aimAngle: number;
    }
  | {
      kind: typeof RaidEvent.LeapTelegraph;
      x: number;
      y: number;
      radius: number;
      remain: number;
      duration: number;
      danger: "yellow" | "red";
    }
  | {
      kind: typeof RaidEvent.LeapLand;
      x: number;
      y: number;
      hits: number;
      danger: "yellow" | "red";
      radius: number;
    }
  | {
      kind: typeof RaidEvent.MeleeImpact;
      x: number;
      y: number;
      hits: number;
      /** Boss melee reach disk (radius + reachPad). */
      radius: number;
    }
  | {
      kind: typeof RaidEvent.Break;
      x: number;
      y: number;
    }
  | {
      kind: typeof RaidEvent.Phase;
      phase: 1 | 2;
    }
  | {
      kind: typeof RaidEvent.Encounter;
      mode: "assault" | "transition" | "boss";
      wave: number;
      bruteIndex: number;
      messageKey: string;
    }
  | {
      kind: typeof RaidEvent.Order;
      orderId:
        | "rally"
        | "focus"
        | "callAdd"
        | "regroup"
        | "retreat"
        | "hold"
        | "dash"
        | "groupDash"
        | "pin"
        | "unpin";
      messageKey: string;
    }
  | {
      kind: typeof RaidEvent.Bark;
      unitId: number;
      x: number;
      y: number;
      messageKey: string;
      /** When set, presentation attaches bubble to bossRoot (unitId ignored for attach). */
      speaker?: "boss";
    }
  | {
      kind: typeof RaidEvent.Spawn;
      unitId: number;
      x: number;
      y: number;
      team: 0 | 1;
    }
  | {
      kind: typeof RaidEvent.Death;
      unitId: number;
      team: 0 | 1;
    }
  | { kind: typeof RaidEvent.Win }
  | { kind: typeof RaidEvent.Lose };
